
import type { GeneratedName, UserInputs } from '../types';
import { AWIN_AFFILIATE_URL_PREFIX, NAMECHEAP_SEARCH_URL_PREFIX } from '../constants';

// Inform TypeScript about the global variables from the CDN scripts
declare const jspdf: any;

// Helper to fetch an image from a URL and convert it to a Base64 string.
// This is necessary to embed external images into the jsPDF document.
const getImageAsBase64 = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob'; // The response is a binary object
    xhr.onload = function () {
      if (this.status === 200) {
        const blob = this.response;
        const reader = new FileReader();
        reader.onloadend = () => {
          // The result is a Data URL (which is Base64 encoded)
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      } else {
        reject(new Error(`Failed to fetch image. Status: ${this.status}`));
      }
    };
    xhr.onerror = reject; // Handle network errors
    xhr.send();
  });
};

export const exportToPdf = async (
  names: GeneratedName[],
  title: string,
  isPro: boolean,
  userEmail?: string,
  inputs?: UserInputs,
) => {
  const { jsPDF } = jspdf;
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'pt',
    format: 'a4'
  });

  const docWidth = doc.internal.pageSize.getWidth();
  const docHeight = doc.internal.pageSize.getHeight();

  // Background
  doc.setFillColor("#FFFFFF");
  doc.rect(0, 0, docWidth, docHeight, 'F');
  
  // --- HEADER ---
  try {
    // FIX: Use relative path to avoid CORS issues on localhost/previews
    const logoUrl = '/brand-name-generator-logo.png';
    const logoBase64 = await getImageAsBase64(logoUrl);
    // Original image is 250x55, so aspect ratio is ~4.54
    const logoHeight = 30;
    const logoWidth = logoHeight * 4.54; 
    doc.addImage(logoBase64, 'PNG', 40, 25, logoWidth, logoHeight);
  } catch (error) {
    console.error("Could not load logo for PDF:", error);
    // Fallback text if logo fails to load
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text("BrandGenius", 40, 40);
  }

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor("#6B7280");
  doc.text("brandgenius.app", docWidth - 40, 40, { align: 'right' });

  // --- DOCUMENT TITLE ---
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor("#1F2937");
  doc.text(title, docWidth / 2, 80, { align: 'center' });

  let startY = 110; // Start content lower down to accommodate the new header
  
  // --- USER CRITERIA SECTION (optional) ---
  if (inputs) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor("#1F2937");
    doc.text("Your Criteria:", 40, startY);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor("#6B7280");
    
    const criteriaItems = [
      userEmail ? `- User: ${userEmail}` : null,
      `- Industry: ${inputs.industry}`,
      `- Keywords: ${inputs.keywords || 'N/A'}`,
      `- Style: ${inputs.style}`,
      `- Length: ${inputs.length}`,
      `- Account Status: ${isPro ? 'Pro User' : 'Free User'}`,
      `- Date: ${new Date().toLocaleDateString()}`
    ].filter(Boolean);

    const criteriaText = criteriaItems.join('\n');
    doc.text(criteriaText, 40, startY + 15);
    // Increase vertical space after criteria section to prevent overlap with the table
    startY += doc.getTextDimensions(criteriaText).h + 40; 
  } else {
     const criteriaItems = [
      userEmail ? `User: ${userEmail}` : null,
      `Account Status: ${isPro ? 'Pro User' : 'Free User'}`,
      `Date: ${new Date().toLocaleDateString()}`
    ].filter(Boolean);
    const criteriaText = criteriaItems.join(' | ');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor("#6B7280");
    doc.text(criteriaText, docWidth / 2, 95, { align: 'center' });
    startY += 10;
  }

  // --- TABLE OF NAMES ---
  const tableColumn = ["Brand Name", "Check Domain Availability"];
  const tableRows: (string | { content: string; data: { url: string } })[][] = [];

  names.forEach(name => {
    const namecheapUrl = `${NAMECHEAP_SEARCH_URL_PREFIX}${name.name}`;
    const affiliateUrl = `${AWIN_AFFILIATE_URL_PREFIX}${encodeURIComponent(namecheapUrl)}`;
    
    const row = [
      name.name,
      { content: 'Click to Check Domain', data: { url: affiliateUrl } },
    ];
    tableRows.push(row);
  });

  (doc as any).autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: startY,
    theme: 'grid',
    headStyles: {
      fillColor: [79, 70, 229], // #4F46E5
      textColor: "#FFFFFF",
      fontStyle: 'bold',
    },
    styles: {
        fillColor: "#FFFFFF",
        textColor: "#1F2937",
        lineColor: "#E5E7EB", // gray-200
        lineWidth: 0.5,
    },
    alternateRowStyles: {
        fillColor: "#F9FAFB", // gray-50
    },
    didParseCell: function (data: any) {
        if (data.column.index === 1 && data.cell.raw && typeof data.cell.raw === 'object' && data.cell.raw.data && data.cell.raw.data.url) {
            data.cell.styles.textColor = [79, 70, 229]; // #4F46E5
            data.cell.styles.fontStyle = 'bold';
        }
    },
    didDrawCell: function (data: any) {
        if (data.column.index === 1 && data.cell.raw && typeof data.cell.raw === 'object' && data.cell.raw.data && data.cell.raw.data.url) {
            const url = data.cell.raw.data.url;
            doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, {
              url: url,
            });
        }
    }
  });

  // --- FOOTER ---
  const pageCount = doc.internal.getNumberOfPages();
  for(let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor("#6B7280");
    
    // Add custom footer note
    const footerNote = "These brand names are generated by BrandGenius.app";
    doc.text(footerNote, docWidth / 2, docHeight - 35, { align: 'center' });
    
    // Page number
    doc.text(`Page ${i} of ${pageCount}`, docWidth / 2, docHeight - 20, {align: 'center'});
  }

  doc.save(`${title.replace(/ /g, '_')}.pdf`);
};
